import React, { useState, useEffect } from "react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/footer";
import {
  successAlert,
  errorAlert,
  warningAlert,
  confirmAlert,
} from "../../utils/alert";
import API from "../../api/api";
import { FaTrash, FaPlus, FaToggleOn, FaToggleOff, FaArrowUp, FaArrowDown, FaFacebook, FaInstagram, FaTwitter, FaYoutube, FaShareAlt } from "react-icons/fa";

const ManageSocialFeeds = () => {
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [postUrl, setPostUrl] = useState("");
  const [platform, setPlatform] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchFeeds = async () => {
    setLoading(true);
    try {
      const res = await API.fetchAdminSocialFeeds();
      setFeeds(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error("Failed to fetch social feeds", err);
      errorAlert("Error", "Failed to load social feeds");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeeds();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!postUrl.trim()) {
      warningAlert("Missing field", "Post URL or embed code is required");
      return;
    }

    setSubmitting(true);
    try {
      await API.createSocialFeed({
        title: title.trim() || null,
        post_url: postUrl.trim(),
        platform: platform || null,
      });
      successAlert("Success", "Live social feed added successfully");
      setTitle("");
      setPostUrl("");
      setPlatform("");
      fetchFeeds();
    } catch (err) {
      console.error("Failed to add feed", err);
      errorAlert("Error", err?.message || "Failed to add feed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (feed) => {
    try {
      await API.updateSocialFeed(feed.id, {
        title: feed.title,
        post_url: feed.post_url,
        platform: feed.platform,
        is_active: !feed.is_active,
      });
      fetchFeeds();
    } catch (err) {
      console.error("Failed to toggle status", err);
      errorAlert("Error", "Failed to update feed status");
    }
  };

  const handleDelete = async (id) => {
    const result = await confirmAlert({
      title: "Remove live feed?",
      text: "This feed item will not be displayed on the homepage",
      confirmText: "Yes, remove",
    });

    if (!result.isConfirmed) return;

    try {
      await API.deleteSocialFeed(id);
      successAlert("Removed", "Feed removed successfully");
      fetchFeeds();
    } catch (err) {
      errorAlert("Error", "Failed to remove feed");
    }
  };

  const handleMove = async (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= feeds.length) return;

    const reordered = [...feeds];
    // Swap elements
    const temp = reordered[index];
    reordered[index] = reordered[nextIndex];
    reordered[nextIndex] = temp;

    // Map new sort orders
    const orders = reordered.map((feed, idx) => ({
      id: feed.id,
      sort_order: idx,
    }));

    // Update local state first for instant response
    setFeeds(reordered);

    try {
      await API.updateSocialFeedOrder(orders);
    } catch (err) {
      console.error("Failed to save reorder", err);
      errorAlert("Error", "Failed to save feed order");
      fetchFeeds(); // rollback
    }
  };

  const getPlatformIcon = (plat) => {
    switch (plat) {
      case "facebook":
        return <FaFacebook className="text-blue-600" size={20} />;
      case "instagram":
        return <FaInstagram className="text-pink-600" size={20} />;
      case "twitter":
        return <FaTwitter className="text-sky-500" size={20} />;
      case "youtube":
        return <FaYoutube className="text-red-600" size={20} />;
      default:
        return <FaShareAlt className="text-slate-500" size={20} />;
    }
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-slate-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 border-b border-slate-200 pb-5">
            <h1 className="text-3xl font-extrabold text-slate-900">Manage Live Social Feeds</h1>
            <p className="text-slate-500 mt-1">
              Add and arrange Facebook, Instagram, Twitter/X, or YouTube posts to show up as a live stream on the homepage.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Add Feed Pane */}
            <div className="lg:col-span-1">
              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm sticky top-6">
                <h2 className="text-lg font-bold text-slate-800 mb-4 pb-2 border-b border-slate-100 flex items-center gap-2">
                  <FaPlus className="text-blue-600" size={14} /> Add Social Post
                </h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1">
                      Post Title (Optional)
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="e.g. Highlights from AP State Para Meet"
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1">
                      Post URL or Raw Iframe Code
                    </label>
                    <textarea
                      rows={4}
                      value={postUrl}
                      onChange={(e) => setPostUrl(e.target.value)}
                      placeholder="Paste the URL (e.g., https://x.com/APParaSports/status/123) OR copy-paste the iframe embed code from the platform."
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1">
                      Platform (Optional, Auto-detected)
                    </label>
                    <select
                      value={platform}
                      onChange={(e) => setPlatform(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Auto-Detect</option>
                      <option value="facebook">Facebook</option>
                      <option value="instagram">Instagram</option>
                      <option value="twitter">Twitter / X</option>
                      <option value="youtube">YouTube</option>
                      <option value="other">Other / Custom Embed</option>
                    </select>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full rounded-lg bg-blue-600 py-2.5 text-sm font-bold text-white hover:bg-blue-700 disabled:bg-blue-400 transition-colors"
                  >
                    {submitting ? "Adding..." : "Add to Homepage Feed"}
                  </button>
                </form>
              </div>
            </div>

            {/* List Feeds */}
            <div className="lg:col-span-2">
              {loading ? (
                <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500">
                  Loading feeds...
                </div>
              ) : feeds.length === 0 ? (
                <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center text-slate-500">
                  No social feeds configured yet. Use the left panel to link social posts.
                </div>
              ) : (
                <div className="space-y-4">
                  {feeds.map((feed, idx) => (
                    <div
                      key={feed.id}
                      className={`bg-white rounded-xl border p-4 shadow-sm flex items-center justify-between gap-4 transition-all ${
                        !feed.is_active ? "opacity-60 bg-slate-50" : ""
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="p-2.5 bg-slate-100 rounded-lg shrink-0">
                          {getPlatformIcon(feed.platform)}
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-bold text-slate-900 truncate">
                            {feed.title || "Untitled Live Feed Item"}
                          </h3>
                          <p className="text-xs text-slate-500 truncate mt-0.5" title={feed.post_url}>
                            {feed.post_url}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        {/* Reorder actions */}
                        <div className="flex flex-col gap-1">
                          <button
                            type="button"
                            onClick={() => handleMove(idx, -1)}
                            disabled={idx === 0}
                            className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent text-slate-600"
                            title="Move Up"
                          >
                            <FaArrowUp size={11} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleMove(idx, 1)}
                            disabled={idx === feeds.length - 1}
                            className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent text-slate-600"
                            title="Move Down"
                          >
                            <FaArrowDown size={11} />
                          </button>
                        </div>

                        {/* Status Toggle */}
                        <button
                          type="button"
                          onClick={() => handleToggle(feed)}
                          className={`p-2 rounded-lg transition-colors ${
                            feed.is_active ? "text-emerald-600 hover:bg-emerald-50" : "text-slate-400 hover:bg-slate-100"
                          }`}
                          title={feed.is_active ? "Active (Shown on Home)" : "Inactive (Hidden)"}
                        >
                          {feed.is_active ? <FaToggleOn size={24} /> : <FaToggleOff size={24} />}
                        </button>

                        {/* Delete */}
                        <button
                          type="button"
                          onClick={() => handleDelete(feed.id)}
                          className="p-2 rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
                          title="Remove post"
                        >
                          <FaTrash size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default ManageSocialFeeds;
